import { Controller } from '@hotwired/stimulus'
import { OPEN_SUPPORT_WIDGET } from './launcher_controller'
import { resetTurboFrame } from 'helpers/turbo_frames'

// Drives the support panel the way the classic Zendesk widget behaved: it opens in the
// corner without blocking the page, keeps whatever was typed when dismissed, and starts
// a fresh form only once a request has actually been sent.
export default class extends Controller {
  static targets = ['frame']

  connect() {
    // The form ships with the page, so a sent request can be swapped back out for the
    // pristine form without another round-trip to the server.
    this.blankFormHtml = this.frameTarget.innerHTML

    document.addEventListener(OPEN_SUPPORT_WIDGET, this.open)
    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('turbo:before-cache', this.prepareForCache)
  }

  disconnect() {
    document.removeEventListener(OPEN_SUPPORT_WIDGET, this.open)
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('turbo:before-cache', this.prepareForCache)
  }

  open = event => {
    this.launcher = event?.detail?.launcher
    this.restoreBlankForm()

    this.element.hidden = false
    this.element.querySelector('[autofocus]')?.focus()
  }

  close = () => {
    this.hide()
    this.launcher?.focus()
  }

  hide = () => {
    this.element.hidden = true
  }

  // Turbo caches the page as we leave it, so put the form back first: a snapshot holding a
  // sent request would come back on the next visit, and be mistaken for the blank form.
  prepareForCache = () => {
    this.restoreBlankForm()
    this.hide()
  }

  get showingConfirmation() {
    return !!this.element.querySelector('[data-support-request-confirmation]')
  }

  restoreBlankForm = () => {
    if (!this.showingConfirmation) return

    // Turbo points the frame at the POST url after a submission, so reset it: the frame keeps
    // the markup we put back rather than chasing the url when next touched. Only reachable once
    // a request has been sent, so there is never a fetch in flight for the reset to cancel.
    resetTurboFrame(this.frameTarget, this.blankFormHtml)
  }

  handleKeydown = event => {
    if (event.key === 'Escape' && !this.element.hidden) this.close()
  }
}
